import React from "react";

import { useEffect, useRef } from "react";
import styles from "./timeout.module.css";

export interface ModalProps {
  isVisible: boolean;
  content: React.JSX.Element[] | React.JSX.Element;
  footer?: string;
  size?: string;
  theme?: string;
  trapFocus?: boolean;
  autoFocus?: boolean;
}

const TimeoutModal = ({
  isVisible = false,
  content,
  footer,
  size = "md",
  theme = "white",
  trapFocus = false,
  autoFocus = false,
}: ModalProps) => {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const trapFocusHandler = (e: KeyboardEvent) => {
    if (e.key === "Tab") {
      const focusableElements = modalRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      if (focusableElements && focusableElements.length > 0) {
        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[
          focusableElements.length - 1
        ] as HTMLElement;

        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    }
  };

  useEffect(() => {
    if (isVisible) {
      document.addEventListener("keydown", trapFocusHandler);

      if (autoFocus && closeButtonRef.current) {
        closeButtonRef.current.focus();
      }

      // Prevent page scrolling when modal is open
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
      document.removeEventListener("keydown", trapFocusHandler);
    }

    return () => {
      document.body.style.overflow = "auto";
      document.removeEventListener("keydown", trapFocusHandler);
    };
  }, [isVisible, autoFocus]);

  return !isVisible ? null : (
    <div
      className={styles.modal}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div
        className={`${styles.modalDialog} ${styles[size]} ${styles[theme]}`}
        onClick={(e) => e.stopPropagation()}
        ref={modalRef}
      >
        <div className={styles.modalBody}>
          <div className={styles.modalContent}>{content}</div>
        </div>
        {footer && <div className={styles.modalFooter}>{footer}</div>}
      </div>
    </div>
  );
};

export default TimeoutModal;
